import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import useSeo from "@/hooks/useSeo";
import { formatDMY } from "@/lib/date";

function groupByMonth(items) {
  const map = {};
  for (const it of items) {
    const [y, m] = it.date.split("-");
    const key = `${y}-${m}`;
    if (!map[key]) map[key] = [];
    map[key].push(it);
  }
  return Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1));
}

function monthLabel(key) {
  const [y, m] = key.split("-");
  const names = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

export default function ResultChart() {
  useSeo({
    title: "Shillong Morning Teer Result Chart | Previous Results",
    description:
      "Shillong Morning Teer Result Chart — a compact monthly table of past FR and SR winning numbers to help you study house ending trends.",
    path: "/result-chart",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Result Chart", path: "/result-chart" },
    ],
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/results", { params: { limit: 5000 } })
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  }, []);

  const grouped = groupByMonth(items);

  return (
    <div className="max-w-xl mx-auto w-full px-4 pt-6 pb-10">
      <h1 className="font-heading font-black text-slate-900 text-3xl sm:text-4xl tracking-tight">
        Shillong Morning Teer Result Chart
      </h1>
      <p className="text-sm text-slate-500 mt-1 mb-5">
        Compact chart of past FR and SR results, grouped by month. Cross-check any number from a
        reliable source before use.
      </p>

      {loading ? (
        <div className="text-center text-slate-500 py-8">Loading…</div>
      ) : grouped.length === 0 ? (
        <Card className="p-8 text-center border-dashed text-slate-500 text-sm">
          No results published yet.
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(([key, rows]) => (
            <div
              key={key}
              className="rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-white"
            >
              <div className="bg-[#0f2748] text-white text-center py-2">
                <div className="text-sm font-heading font-black tracking-wide">
                  {monthLabel(key)}
                </div>
              </div>
              <div className="grid grid-cols-3 bg-emerald-500 text-white text-[12px] font-heading font-black uppercase tracking-wide">
                <div className="text-center py-2 border-r border-white/30">Date</div>
                <div className="text-center py-2 border-r border-white/30">F/R</div>
                <div className="text-center py-2">S/R</div>
              </div>
              {rows.map((it, i) => (
                <div
                  key={it.id}
                  className={`grid grid-cols-3 text-center ${
                    i % 2 === 0 ? "bg-white" : "bg-slate-50"
                  }`}
                >
                  <div className="py-2.5 text-sm font-semibold text-slate-700 border-r border-slate-200">
                    {formatDMY(it.date)}
                  </div>
                  <div className="py-2.5 font-numbers font-bold text-slate-900 border-r border-slate-200">
                    {it.r1 ? String(it.r1).padStart(2, "0") : "--"}
                  </div>
                  <div className="py-2.5 font-numbers font-bold text-slate-900">
                    {it.r2 ? String(it.r2).padStart(2, "0") : "--"}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 text-sm text-slate-600 leading-relaxed">
        Explore more:{" "}
        <Link to="/previous-results" className="text-sky-700 font-semibold underline">
          Previous Results
        </Link>
        {" · "}
        <Link to="/common-number" className="text-sky-700 font-semibold underline">
          Common Number
        </Link>
        {" · "}
        <Link to="/target-number" className="text-sky-700 font-semibold underline">
          Target Number
        </Link>
        {" · "}
        <Link to="/teer-calendar" className="text-sky-700 font-semibold underline">
          Teer Calendar
        </Link>
        .
      </div>
    </div>
  );
}
